/**
 * KAN-858: кто ведёт переписку ("Ведёт: бот" / "Ведёт: менеджер (ручной
 * режим)") и кнопки "Взять переписку" / "Вернуть боту" (с подтверждением).
 * Без права менять клиента: только строка и подсказка про чтение.
 */
import { Bot, Hand, UserRound } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  CHAT_LEAD_BOT,
  CHAT_LEAD_MANAGER,
  READ_ONLY_HINT,
  RETURN_CONFIRM_TEXT,
  RETURN_TO_BOT_LABEL,
  TAKE_OVER_LABEL,
} from '@/lib/mobileChat';
import type { MobileManualChat } from '@/hooks/useMobileManualChat';

const BTN = 'flex min-h-[44px] items-center justify-center gap-2 rounded-xl px-4 text-sm font-medium disabled:opacity-50';

type Props = {
  chat: Pick<
    MobileManualChat,
    | 'canWrite'
    | 'manualMode'
    | 'canReturn'
    | 'busy'
    | 'takeOver'
    | 'returnAsked'
    | 'askReturn'
    | 'cancelReturn'
    | 'confirmReturn'
  >;
};

export function MobileChatControl({ chat }: Props): JSX.Element {
  const LeadIcon = chat.manualMode ? UserRound : Bot;
  return (
    <div className="space-y-2" data-testid="mobile-chat-control">
      <p
        className={cn(
          'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-medium',
          chat.manualMode ? 'bg-amber-100 text-amber-900' : 'bg-sky-50 text-sky-900'
        )}
        data-testid="mobile-chat-lead"
      >
        <LeadIcon className="h-4 w-4" aria-hidden="true" />
        {chat.manualMode ? CHAT_LEAD_MANAGER : CHAT_LEAD_BOT}
      </p>
      {!chat.canWrite && (
        <p className="text-sm text-gray-500" data-testid="mobile-chat-read-only">
          {READ_ONLY_HINT}
        </p>
      )}
      {chat.canWrite && (
        <div className="flex flex-wrap gap-2">
          {!chat.manualMode && (
            <button
              type="button"
              onClick={chat.takeOver}
              disabled={chat.busy}
              className={cn(BTN, 'bg-blue-600 text-white')}
              data-testid="mobile-chat-take-over"
            >
              <Hand className="h-4 w-4" aria-hidden="true" />
              {TAKE_OVER_LABEL}
            </button>
          )}
          {chat.canReturn && !chat.returnAsked && (
            <button
              type="button"
              onClick={chat.askReturn}
              disabled={chat.busy}
              className={cn(BTN, 'border border-gray-300 bg-white text-gray-900')}
              data-testid="mobile-chat-return"
            >
              <Bot className="h-4 w-4" aria-hidden="true" />
              {RETURN_TO_BOT_LABEL}
            </button>
          )}
        </div>
      )}
      {chat.canWrite && chat.returnAsked && (
        <div className="space-y-2 rounded-lg bg-gray-50 p-3" role="alertdialog" aria-label="Вернуть боту">
          <p className="text-sm text-gray-800">{RETURN_CONFIRM_TEXT}</p>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={chat.confirmReturn}
              className={cn(BTN, 'bg-blue-600 text-white')}
              data-testid="mobile-chat-return-confirm"
            >
              Да, вернуть
            </button>
            <button
              type="button"
              onClick={chat.cancelReturn}
              className={cn(BTN, 'border border-gray-300 bg-white text-gray-900')}
            >
              Нет
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
